import { useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";
import router from "next/router";
import { AppContext } from "@/lib/contexts";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { Modal } from "components/atoms/Modal";
import Spinner from "components/atoms/Spinner";
import { editImage, poll, pollEditImage } from "lib/actions";
import { AppReducerActionType, EditType } from "types";
import { Span, Title } from "utils/styling/typography.styled";
import * as Styled from "./InpaintingModal.styled";
interface InpaintingModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit?: (editRequest: string, isPremium: boolean, editType: string, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string) => void;
  base64Image: string; // Base64 image data to display the masked area
  originalImage: string;
  imageId: string;
  handleInpaintClick: () => void;
}
export const InpaintingModal: React.FC<InpaintingModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  base64Image,
  originalImage,
  imageId,
  handleInpaintClick
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [description, setDescription] = useState<string>("");
  const [negativePrompt, setNegativePrompt] = useState<string>(""); // Negative prompt
  const [loading, setLoading] = useState(false); // Loading state

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };
  const handleNegativePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNegativePrompt(e.target.value);
  };
  const handleEditSubmit = useCallback(async (editRequest: string, negativePrompt: string, imageId: string, editType: string, base64Image: string) => {
    const formData = new FormData();
    formData.append("editRequest", editRequest); // Add the prompt
    formData.append("imageId", imageId); // Add the image ID
    formData.append("editType", editType); // Add the edit type

    formData.append("base64Image", base64Image);
    if (negativePrompt) {
      formData.append("negativePrompt", negativePrompt);
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await editImage(formData);
    if (error) {
      toast.error("An error occurred while submitting the edit request.");
      setLoading(false);
      return;
    }
    if (requestId) {
      const {
        image,
        error
      } = await poll(async () => {
        const result = await pollEditImage(requestId);
        if (result.error && typeof result.error === "object") {
          console.log("Error details:", result.error);
        }
        return result;
      }, ({
        image,
        error
      }) => {
        // Continue polling only if there is no image and no critical error
        if (!!image) return false; // Stop polling when an image is retrieved
        if (typeof error === "string") return false;
        if (typeof error === "object") return false;
        return true; // Continue polling otherwise
      }, 5000);
      if (error) {
        if (typeof error === "object") {
          toast.error(error.message || "An unknown error occurred while generating the image and your gold has been refunded. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 1
            }
          });
        } else if (typeof error === "string") {
          toast.error("The request timed out. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 1
            }
          });
        }
      }
      if (image && image.length > 0) {
        const newImage = Array.isArray(image) ? image[0] : image;

        // Redirect to the new image's page
        router.push(`/images/${newImage.id}`);
      }
    }
    setLoading(false);
  }, [dispatch, state]);
  const handleSubmit = async () => {
    if (onSubmit) {
      await onSubmit(description, false, EditType.INPAINTING, negativePrompt, undefined, undefined, undefined, base64Image);
      onCancel();
    } else {
      await handleEditSubmit(description, negativePrompt, imageId, EditType.INPAINTING, base64Image);
      onCancel();
    }
  };
  const handleEditMaskClick = () => {
    onCancel();
    handleInpaintClick();
  };
  const handleCancel = () => {
    setLoading(false);
    setDescription("");
    setNegativePrompt("");
    onCancel();
  };
  return <Modal open={isOpen} handleBackdropClick={onCancel} disableBackdropClick data-sentry-element="Modal" data-sentry-component="InpaintingModal" data-sentry-source-file="index.tsx">
            <Styled.ModalContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">Inpainting Edit</Title>
                <hr />
                <Styled.ImageWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.OriginalImage src={originalImage} alt="Original Image" data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    <Styled.ImagePreview src={base64Image} alt="Masked Area" data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Styled.ImageWrapper>
                <Styled.InlineButton onClick={handleEditMaskClick} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Edit Masked Area</Span>
                </Styled.InlineButton>
                <Styled.FormGroup data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.Label htmlFor="description" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        Positive Prompt:
                    </Styled.Label>
                    <Styled.TextArea id="description" name="description" value={description} onChange={handleDescriptionChange} placeholder="Enter a description of the changes you want. Only describe the change, not how to make it. Eg. 'Wooden Staff' instead of 'Change the xx to a wooden staff'." data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Styled.FormGroup>
                <Styled.FormGroup data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.Label htmlFor="negativePrompt" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        Negative Prompt:
                    </Styled.Label>
                    <Styled.TextArea id="negativePrompt" name="negativePrompt" value={negativePrompt} onChange={handleNegativePromptChange} placeholder="Enter what you do not want in the image. Eg. 'Low Resolution, Low Quality'" data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Styled.FormGroup>
                <Styled.GoldCost data-sentry-element="unknown" data-sentry-source-file="index.tsx">Gold Cost: 1 Gold</Styled.GoldCost>
                <Styled.ButtonContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Button type="button" variant={ButtonType.SUCCESS} onClick={handleSubmit} disabled={loading} // Disable the button while loading
        data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {loading ? <Spinner size={24} color="inherit" /> // Show spinner when loading
          : <Span>Submit</Span>}
                    </Button>
                    <Button type="button" variant={ButtonType.PRIMARY} onClick={handleCancel} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Cancel</Span>
                    </Button>
                </Styled.ButtonContainer>
            </Styled.ModalContainer>
        </Modal>;
};